/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/clarity.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { api } from './api';

declare global {
  interface Window {
    clarity?: ((action: string, ...args: any[]) => void) & { q?: any[][] };
  }
}

export interface ClarityClientEventEntry {
  id: string;
  ts: string;
  apiMethod: 'identify' | 'set' | 'event' | 'upgrade';
  name: string;
  tags: Record<string, string>;
  detail: string;
}

export const DEFAULT_CLARITY_PROJECT_ID = 'kontor-sme-twin-clarity';
const MAX_LOG_SIZE = 200;
let activeProjectId = DEFAULT_CLARITY_PROJECT_ID;
const eventLog: ClarityClientEventEntry[] = [];
const activeTags: Record<string, string> = {};

function pushEntry(entry: Omit<ClarityClientEventEntry, 'id' | 'ts'>): ClarityClientEventEntry {
  const full: ClarityClientEventEntry = {
    ...entry,
    id: `cl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    ts: new Date().toISOString(),
  };
  eventLog.unshift(full);
  if (eventLog.length > MAX_LOG_SIZE) eventLog.length = MAX_LOG_SIZE;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kontor-clarity-event', { detail: full }));
  }
  return full;
}

/** Ensures the local `window.clarity` queue function exists. No external script is ever loaded. */
export function initClarity(projectId?: string) {
  if (projectId && projectId.trim()) {
    activeProjectId = projectId.trim();
  }
  if (typeof window === 'undefined') return activeProjectId;

  if (!window.clarity) {
    const fn = function (...args: any[]) {
      (fn.q = fn.q || []).push(args);
    } as NonNullable<Window['clarity']>;
    window.clarity = fn;
  }

  // Never loads the Microsoft Clarity tag: session replay of a fiduciary application would disclose financial
  // screens to a US processor without consent (KONTOR-ADP-001 R-04). The queue above only feeds the in-app event log.
  return activeProjectId;
}

export function clarityIdentify(
  customUserId: string,
  customSessionId?: string,
  customPageId?: string,
  friendlyName?: string,
) {
  initClarity();
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity('identify', customUserId, customSessionId, customPageId, friendlyName);
  }
  activeTags.persona_actor = customUserId;
  if (customSessionId) activeTags.clarity_session_id = customSessionId;
  if (customPageId) activeTags.clarity_page_id = customPageId;
  if (friendlyName) activeTags.persona_friendly_name = friendlyName;

  return pushEntry({
    apiMethod: 'identify',
    name: `identify(${customUserId})`,
    tags: { ...activeTags },
    detail: `Digital Twin identifiziert: ${friendlyName ?? customUserId} (Session ${customSessionId ?? 'auto'})`,
  });
}

export function claritySetTag(key: string, value: string | string[]) {
  initClarity();
  const strVal = Array.isArray(value) ? value.join(',') : String(value);
  activeTags[key] = strVal;
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity('set', key, value);
  }
  return pushEntry({
    apiMethod: 'set',
    name: `set(${key}=${strVal})`,
    tags: { [key]: strVal },
    detail: `Clarity Tag gesetzt: ${key} = ${strVal}`,
  });
}

export function clarityEvent(eventName: string, tags: Record<string, string> = {}, detail?: string) {
  initClarity();
  for (const [k, v] of Object.entries(tags)) {
    activeTags[k] = v;
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('set', k, v);
    }
  }
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity('event', eventName);
  }
  return pushEntry({
    apiMethod: 'event',
    name: eventName,
    tags: { ...activeTags, ...tags },
    detail: detail ?? `MS Clarity Event: ${eventName}`,
  });
}

export function clarityUpgrade(reason: string) {
  initClarity();
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity('upgrade', reason);
  }
  return pushEntry({
    apiMethod: 'upgrade',
    name: `upgrade(${reason})`,
    tags: { ...activeTags, upgrade_reason: reason },
    detail: `Session für menschliche Stichprobenprüfung priorisiert: ${reason}`,
  });
}

export function getClarityEventLog(): ClarityClientEventEntry[] {
  return [...eventLog];
}

export function getActiveClarityProjectId(): string {
  return activeProjectId;
}

export async function flushClarityEventsToBackend(
  tid: string,
  sessionId: string,
  personaId?: string,
  route?: string,
  eventsToSync?: ClarityClientEventEntry[],
) {
  const batch = (eventsToSync ?? eventLog.slice(0, 25)).map((e) => ({
    timestamp: e.ts,
    eventType: e.apiMethod,
    name: e.name,
    tags: {
      ...e.tags,
      ...(personaId ? { twin_persona_id: personaId } : {}),
      ...(route ? { ui_route: route } : {}),
    },
    detail: e.detail,
  }));
  return await api.post(`/api/t/${tid}/digital-twins/clarity/events`, {
    sessionId,
    personaId,
    route: route ?? (typeof window !== 'undefined' ? window.location.pathname : '/digital-twins'),
    events: batch,
  });
}

export type TwinPlaybackMode = 'auto' | 'step' | 'realtime_2h';

export interface TwinAutopilotHudState {
  personaName: string;
  stepIndex: number;
  totalSteps: number;
  titleDe: string;
  route: string;
  actionSummaryDe: string;
  healedIssueDe?: string;
  stepCode?: string;
  humanReadableScript?: string;
  mode?: TwinPlaybackMode;
  interactiveControls?: boolean;
  clarityEventName?: string;
  clarityTags?: Record<string, string>;
  clarityDetail?: string;
  realtimeElapsedSec?: number;
  realtimeTotalSec?: number;
}

const HUD_ELEMENT_ID = 'kontor-digital-twin-autopilot-hud';

interface ActiveTourSession {
  active: boolean;
  steps: TwinAutopilotHudState[];
  currentIndex: number;
  mode: TwinPlaybackMode;
  autoDelayMs: number;
  autoTimer: ReturnType<typeof setTimeout> | null;
  startedAtMs: number;
  realtimeTotalMs: number;
  visitedIndices: Set<number>;
  onStepActivate?: (step: TwinAutopilotHudState, index: number) => void;
  onFinish?: () => void;
  resolvePromise?: () => void;
}

let activeTour: ActiveTourSession | null = null;
let keyListenerAttached = false;

function escapeHtml(raw: string): string {
  return String(raw)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function emitTourStateChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kontor-twin-tour-state', { detail: getInteractiveTwinTourStatus() }));
  }
}

export function getInteractiveTwinTourStatus() {
  if (!activeTour || !activeTour.active) {
    return {
      active: false,
      mode: 'auto' as TwinPlaybackMode,
      currentIndex: 0,
      totalSteps: 0,
      currentStep: null as TwinAutopilotHudState | null,
    };
  }
  return {
    active: true,
    mode: activeTour.mode,
    currentIndex: activeTour.currentIndex,
    totalSteps: activeTour.steps.length,
    currentStep: activeTour.steps[activeTour.currentIndex] ?? null,
  };
}

function clearTourAutoTimer() {
  if (activeTour?.autoTimer) {
    clearTimeout(activeTour.autoTimer);
    activeTour.autoTimer = null;
  }
}

function scheduleNextAutoStepIfNeeded() {
  clearTourAutoTimer();
  if (!activeTour || !activeTour.active || activeTour.mode === 'step') return;
  const isRealtime2h = activeTour.mode === 'realtime_2h';
  const delayMs = isRealtime2h ? Math.max(15000, activeTour.autoDelayMs) : activeTour.autoDelayMs;

  activeTour.autoTimer = setTimeout(() => {
    if (!activeTour || !activeTour.active || activeTour.mode === 'step') return;
    const elapsedMs = Date.now() - activeTour.startedAtMs;
    if (activeTour.currentIndex < activeTour.steps.length - 1) {
      activeTour.currentIndex++;
      activateCurrentTourStep();
    } else if (isRealtime2h && elapsedMs < activeTour.realtimeTotalMs) {
      // In 2h real-human-time marathon mode, continue cycling through the accountant's daily tasks until the 2h window completes
      activeTour.currentIndex = 0;
      activateCurrentTourStep();
    } else {
      stopInteractiveTwinTour();
    }
  }, delayMs);
}

function activateCurrentTourStep() {
  if (!activeTour || !activeTour.active) return;
  clearTourAutoTimer();
  const idx = Math.max(0, Math.min(activeTour.steps.length - 1, activeTour.currentIndex));
  activeTour.currentIndex = idx;
  const step = activeTour.steps[idx];
  if (!step) return;

  activeTour.onStepActivate?.(step, idx);

  if (!activeTour.visitedIndices.has(idx) && step.clarityEventName) {
    activeTour.visitedIndices.add(idx);
    clarityEvent(step.clarityEventName, step.clarityTags ?? {}, step.clarityDetail);
  }

  const elapsedSec = Math.floor((Date.now() - activeTour.startedAtMs) / 1000);
  const totalSec = Math.floor(activeTour.realtimeTotalMs / 1000);

  showTwinAutopilotHud({
    ...step,
    stepIndex: idx + 1,
    totalSteps: activeTour.steps.length,
    mode: activeTour.mode,
    interactiveControls: true,
    realtimeElapsedSec: elapsedSec,
    realtimeTotalSec: totalSec,
  });

  emitTourStateChange();
  scheduleNextAutoStepIfNeeded();
}

export function stepTwinTourNext() {
  if (!activeTour || !activeTour.active) return;
  clearTourAutoTimer();
  if (activeTour.currentIndex < activeTour.steps.length - 1) {
    activeTour.currentIndex++;
    activateCurrentTourStep();
  } else {
    stopInteractiveTwinTour();
  }
}

export function stepTwinTourPrev() {
  if (!activeTour || !activeTour.active) return;
  clearTourAutoTimer();
  // Switching backward puts the user in manual step-by-step control so it doesn't immediately jump forward again
  activeTour.mode = 'step';
  if (activeTour.currentIndex > 0) {
    activeTour.currentIndex--;
  }
  activateCurrentTourStep();
}

export function toggleTwinTourMode(): TwinPlaybackMode {
  if (!activeTour || !activeTour.active) return 'step';
  activeTour.mode = activeTour.mode === 'step' ? 'auto' : 'step';
  const step = activeTour.steps[activeTour.currentIndex];
  if (step) {
    showTwinAutopilotHud({
      ...step,
      stepIndex: activeTour.currentIndex + 1,
      totalSteps: activeTour.steps.length,
      mode: activeTour.mode,
      interactiveControls: true,
      realtimeElapsedSec: Math.floor((Date.now() - activeTour.startedAtMs) / 1000),
      realtimeTotalSec: Math.floor(activeTour.realtimeTotalMs / 1000),
    });
  }
  emitTourStateChange();
  if (activeTour.mode !== 'step') {
    scheduleNextAutoStepIfNeeded();
  } else {
    clearTourAutoTimer();
  }
  return activeTour.mode;
}

export function setTwinTourMode(mode: TwinPlaybackMode) {
  if (!activeTour || !activeTour.active) return;
  activeTour.mode = mode;
  activateCurrentTourStep();
}

export function jumpToTwinTourStep(index: number) {
  if (!activeTour || !activeTour.active) return;
  clearTourAutoTimer();
  activeTour.mode = 'step';
  activeTour.currentIndex = Math.max(0, Math.min(activeTour.steps.length - 1, index));
  activateCurrentTourStep();
}

export function stopInteractiveTwinTour() {
  clearTourAutoTimer();
  if (keyListenerAttached && typeof window !== 'undefined') {
    window.removeEventListener('keydown', handleGlobalTourKeyDown, true);
    keyListenerAttached = false;
  }
  const session = activeTour;
  activeTour = null;
  hideTwinAutopilotHud();
  emitTourStateChange();
  if (session) {
    try {
      session.onFinish?.();
    } finally {
      session.resolvePromise?.();
    }
  }
}

function handleGlobalTourKeyDown(e: KeyboardEvent) {
  if (!activeTour || !activeTour.active) return;
  const target = e.target as HTMLElement | null;
  if (
    target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable)
  ) {
    return;
  }
  if (e.metaKey || e.ctrlKey || e.altKey) return;

  if (e.key === '>' || e.key === '.' || e.key === 'ArrowRight') {
    e.preventDefault();
    e.stopPropagation();
    stepTwinTourNext();
  } else if (e.key === '<' || e.key === ',' || e.key === 'ArrowLeft') {
    e.preventDefault();
    e.stopPropagation();
    stepTwinTourPrev();
  } else if (e.key === ' ' || e.code === 'Space') {
    e.preventDefault();
    e.stopPropagation();
    toggleTwinTourMode();
  } else if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    stopInteractiveTwinTour();
  }
}

export function startInteractiveTwinTour(options: {
  steps: TwinAutopilotHudState[];
  initialMode: TwinPlaybackMode;
  startIndex?: number;
  autoDelayMs?: number;
  realtimeTotalMs?: number;
  onStepActivate?: (step: TwinAutopilotHudState, index: number) => void;
  onFinish?: () => void;
}): Promise<void> {
  if (activeTour?.active) {
    clearTourAutoTimer();
    activeTour.resolvePromise?.();
  }

  return new Promise<void>((resolve) => {
    activeTour = {
      active: true,
      steps: options.steps,
      currentIndex: Math.max(0, Math.min(options.steps.length - 1, options.startIndex ?? 0)),
      mode: options.initialMode,
      autoDelayMs: options.autoDelayMs ?? 1200,
      autoTimer: null,
      startedAtMs: Date.now(),
      realtimeTotalMs: options.realtimeTotalMs ?? 7200000,
      visitedIndices: new Set<number>(),
      onStepActivate: options.onStepActivate,
      onFinish: options.onFinish,
      resolvePromise: resolve,
    };

    if (typeof window !== 'undefined' && !keyListenerAttached) {
      window.addEventListener('keydown', handleGlobalTourKeyDown, true);
      keyListenerAttached = true;
    }

    activateCurrentTourStep();
  });
}

function fmtHms(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

export function showTwinAutopilotHud(state: TwinAutopilotHudState) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(HUD_ELEMENT_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = HUD_ELEMENT_ID;
    el.style.cssText = [
      'position:fixed',
      'left:24px',
      'bottom:24px',
      'z-index:9999',
      'max-width:580px',
      'width:calc(100vw - 48px)',
      'background:rgba(15, 23, 42, 0.97)',
      'color:#f8fafc',
      'border:1px solid rgba(56, 189, 248, 0.5)',
      'border-radius:14px',
      'padding:14px 16px',
      'box-shadow:0 24px 55px rgba(2, 6, 23, 0.65)',
      'font-family:system-ui,-apple-system,sans-serif',
      'backdrop-filter:blur(12px)',
      'transition:all 0.2s ease',
      'pointer-events:auto',
    ].join(';');
    el.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement | null)?.closest('[data-tour-action]') as HTMLElement | null;
      if (!btn) return;
      ev.preventDefault();
      ev.stopPropagation();
      const action = btn.getAttribute('data-tour-action');
      if (action === 'prev') stepTwinTourPrev();
      else if (action === 'next') stepTwinTourNext();
      else if (action === 'toggle-mode') toggleTwinTourMode();
      else if (action === 'finish') stopInteractiveTwinTour();
      else if (action === 'jump') {
        const idx = Number(btn.getAttribute('data-step-idx') ?? '0');
        if (!Number.isNaN(idx)) jumpToTwinTourStep(idx);
      }
    });
    document.body.appendChild(el);
  }

  const isStepMode = state.mode === 'step';
  const isRealtime2h = state.mode === 'realtime_2h';
  const pct = Math.max(6, Math.min(100, Math.round((state.stepIndex / Math.max(1, state.totalSteps)) * 100)));
  const isFirst = state.stepIndex <= 1;
  const isLast = state.stepIndex >= state.totalSteps;
  const stepCodeBadge = state.stepCode ?? `STEP-${String(state.stepIndex).padStart(2, '0')}`;
  const modeLabel = isStepMode
    ? 'Schritt-für-Schritt (Manuell)'
    : isRealtime2h
      ? `2h Echtzeit-Arbeitstag (${fmtHms(state.realtimeElapsedSec ?? 0)} / ${fmtHms(state.realtimeTotalSec ?? 7200)})`
      : 'Live-Autopilot (Auto)';

  const stepDotsHtml =
    state.interactiveControls && state.totalSteps > 1
      ? `<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:10px;">
          ${Array.from({ length: state.totalSteps }, (_, i) => {
            const active = i + 1 === state.stepIndex;
            const done = i + 1 < state.stepIndex;
            const bg = active ? '#38bdf8' : done ? 'rgba(16,185,129,0.28)' : 'rgba(148,163,184,0.16)';
            const fg = active ? '#0f172a' : done ? '#6ee7b7' : '#94a3b8';
            return `<button type="button" data-tour-action="jump" data-step-idx="${i}" title="Zu Schritt ${i + 1} springen" style="cursor:pointer;border:none;border-radius:6px;padding:2px 7px;font-size:10px;font-weight:700;font-family:monospace;background:${bg};color:${fg};">${i + 1}</button>`;
          }).join('')}
        </div>`
      : '';

  const scriptHtml = state.humanReadableScript
    ? `<pre style="margin:0 0 9px 0;padding:8px 10px;border-radius:8px;background:rgba(2,6,23,0.85);border:1px solid rgba(148,163,184,0.25);color:#e2e8f0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:10.5px;line-height:1.45;overflow-x:auto;white-space:pre-wrap;">${escapeHtml(state.humanReadableScript)}</pre>`
    : '';

  const controlsHtml = state.interactiveControls
    ? `<div style="display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:6px;margin-top:10px;padding-top:9px;border-top:1px solid rgba(148,163,184,0.22);">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <button type="button" data-tour-action="prev" ${isFirst ? 'disabled' : ''} style="cursor:${isFirst ? 'not-allowed' : 'pointer'};opacity:${isFirst ? '0.45' : '1'};border:1px solid rgba(148,163,184,0.35);background:rgba(30,41,59,0.9);color:#f8fafc;border-radius:7px;padding:5px 10px;font-size:11.5px;font-weight:600;display:inline-flex;align-items:center;gap:5px;">
            Zurück <kbd style="font-size:10px;background:rgba(15,23,42,0.8);padding:1px 5px;border-radius:4px;border:1px solid rgba(148,163,184,0.3);">&lt;</kbd>
          </button>
          <button type="button" data-tour-action="toggle-mode" style="cursor:pointer;border:1px solid ${isStepMode ? 'rgba(16,185,129,0.5)' : 'rgba(245,158,11,0.5)'};background:${isStepMode ? 'rgba(16,185,129,0.18)' : 'rgba(245,158,11,0.18)'};color:${isStepMode ? '#6ee7b7' : '#fde68a'};border-radius:7px;padding:5px 10px;font-size:11.5px;font-weight:600;display:inline-flex;align-items:center;gap:5px;">
            ${isStepMode ? 'Auto-Play' : 'Pause'} <kbd style="font-size:10px;background:rgba(15,23,42,0.8);padding:1px 5px;border-radius:4px;border:1px solid rgba(148,163,184,0.3);">Space</kbd>
          </button>
          <button type="button" data-tour-action="next" style="cursor:pointer;border:1px solid rgba(56,189,248,0.6);background:linear-gradient(135deg,#0284c7,#2563eb);color:#ffffff;border-radius:7px;padding:5px 12px;font-size:11.5px;font-weight:700;display:inline-flex;align-items:center;gap:5px;box-shadow:0 4px 12px rgba(37,99,235,0.35);">
            ${isLast ? 'Fertig & Auswertung' : 'Nächster Schritt →'} <kbd style="font-size:10px;background:rgba(15,23,42,0.45);padding:1px 5px;border-radius:4px;border:1px solid rgba(255,255,255,0.3);">&gt;</kbd>
          </button>
        </div>
        <button type="button" data-tour-action="finish" style="cursor:pointer;border:1px solid rgba(148,163,184,0.3);background:transparent;color:#cbd5e1;border-radius:7px;padding:5px 9px;font-size:11px;display:inline-flex;align-items:center;gap:4px;">
          Beenden <kbd style="font-size:9.5px;background:rgba(15,23,42,0.8);padding:1px 4px;border-radius:4px;">Esc</kbd>
        </button>
      </div>`
    : '';

  el.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;flex-wrap:wrap;">
      <div style="display:flex;align-items:center;gap:7px;flex-wrap:wrap;">
        <span style="display:inline-block;width:9px;height:9px;border-radius:999px;background:${isStepMode ? '#f59e0b' : isRealtime2h ? '#e9c880' : '#38bdf8'};box-shadow:0 0 10px ${isStepMode ? '#f59e0b' : isRealtime2h ? '#e9c880' : '#38bdf8'};"></span>
        <span style="font-size:11px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:${isStepMode ? '#fbbf24' : isRealtime2h ? '#d8b4fe' : '#38bdf8'};">
          ${modeLabel} · ${state.stepIndex}/${state.totalSteps}
        </span>
        <span style="font-size:10px;font-family:monospace;font-weight:700;background:rgba(56,189,248,0.16);color:#7dd3fc;border:1px solid rgba(56,189,248,0.35);padding:1px 6px;border-radius:6px;">
          ${escapeHtml(stepCodeBadge)}
        </span>
      </div>
      <span style="font-size:10px;font-family:monospace;background:rgba(16,185,129,0.18);color:#34d399;border:1px solid rgba(16,185,129,0.35);padding:2px 7px;border-radius:999px;">
        ● MS Clarity ${activeProjectId}
      </span>
    </div>
    ${stepDotsHtml}
    <div style="font-size:13.5px;font-weight:700;color:#ffffff;margin-bottom:4px;">
      ${escapeHtml(state.personaName)} — ${escapeHtml(state.titleDe)}
    </div>
    <div style="font-size:12px;color:#cbd5e1;line-height:1.45;margin-bottom:7px;">
      ${escapeHtml(state.actionSummaryDe)}
    </div>
    ${scriptHtml}
    ${
      state.healedIssueDe
        ? `<div style="font-size:11px;color:#fde68a;background:rgba(245,158,11,0.16);border:1px solid rgba(245,158,11,0.35);padding:5px 8px;border-radius:8px;margin-bottom:8px;">
             ✓ Verifiziert / Geheilt: ${escapeHtml(state.healedIssueDe)}
           </div>`
        : ''
    }
    <div style="display:flex;align-items:center;justify-content:space-between;font-size:10px;color:#94a3b8;font-family:monospace;margin-bottom:5px;">
      <span>UI-Route: ${escapeHtml(state.route)} · Tasten: [&lt;] Zurück · [Space] Pause/Auto · [&gt;] Weiter · [Esc] Ende</span>
      <span>${pct}%</span>
    </div>
    <div style="width:100%;height:5px;background:rgba(148,163,184,0.22);border-radius:999px;overflow:hidden;">
      <div style="width:${pct}%;height:100%;background:linear-gradient(90deg,#38bdf8,#10b981);transition:width 0.25s ease;"></div>
    </div>
    ${controlsHtml}
  `;
}

export function hideTwinAutopilotHud() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(HUD_ELEMENT_ID);
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

